import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useLang } from "@/context/LangContext";
import {
  FileText,
  Send,
  FileSignature,
  MessageSquare,
  GitPullRequest,
  ClipboardList,
  Receipt,
  ShieldCheck,
  CheckCircle2,
  Circle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Shared vocabulary — only three approved statuses.
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  live: {
    labelKey: "dashboard.status_live",
    className:
      "bg-emerald-100 text-emerald-800 border border-emerald-200",
  },
  "coming-next": {
    labelKey: "dashboard.status_coming_next",
    className:
      "bg-[var(--jc-bg)] text-[var(--jc-text-soft)] border border-[var(--jc-border)]",
  },
};

const StatusPill = ({ status, testid }) => {
  const { t } = useLang();
  const s = STATUS_STYLES[status] || STATUS_STYLES["coming-next"];
  return (
    <span
      data-testid={testid}
      className={`inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase ${s.className}`}
    >
      {t(s.labelKey)}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Dashboard "Full workflow" strip
// ---------------------------------------------------------------------------
const DASHBOARD_STEPS = [
  { key: "estimate", labelKey: "dashboard.step_estimate", icon: FileText, status: "live", to: "/app/estimates" },
  { key: "proposal", labelKey: "dashboard.step_proposal", icon: Send, status: "live", to: "/app/proposals" },
  { key: "contract", labelKey: "dashboard.step_contract", icon: FileSignature, status: "live", to: "/app/contracts" },
  { key: "customer-updates", labelKey: "dashboard.step_customer_updates", icon: MessageSquare, status: "live", to: "/app/customer-updates" },
  { key: "change-orders", labelKey: "dashboard.step_change_orders", icon: GitPullRequest, status: "coming-next" },
  { key: "work-orders", labelKey: "dashboard.step_work_orders", icon: ClipboardList, status: "live", to: "/app/work-orders" },
  { key: "invoice", labelKey: "dashboard.step_invoice", icon: Receipt, status: "live", to: "/app/invoices" },
  { key: "closeout", labelKey: "dashboard.step_closeout", icon: ShieldCheck, status: "live", to: "/app/closeout" },
];

const StepNode = ({ step, testidPrefix }) => {
  const { t } = useLang();
  const isLive = step.status === "live";
  const Icon = step.icon;
  const body = (
    <div
      data-testid={`${testidPrefix}-node-${step.key}`}
      className={`h-full flex flex-col justify-between gap-3 p-4 rounded-lg border transition ${
        isLive
          ? "bg-white border-[var(--jc-border)] hover:border-[var(--jc-blue)]/60 hover:shadow-md"
          : "bg-[var(--jc-bg)] border-dashed border-[var(--jc-border)]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
            isLive ? "bg-[var(--jc-navy)]/5 text-[var(--jc-navy)]" : "bg-white text-[var(--jc-text-soft)]"
          }`}
        >
          <Icon size={14} />
        </span>
        <div className="text-xs font-semibold leading-tight text-[var(--jc-text)]">{t(step.labelKey)}</div>
      </div>
      <StatusPill status={step.status} testid={`${testidPrefix}-status-${step.key}`} />
    </div>
  );
  if (isLive && step.to) {
    return (
      <Link to={step.to} className="block h-full" data-testid={`${testidPrefix}-link-${step.key}`}>
        {body}
      </Link>
    );
  }
  return body;
};

export const WorkflowStrip = () => {
  const { t } = useLang();
  return (
  <section
    data-testid="dashboard-workflow-strip"
    className="jc-card p-6 mb-6"
  >
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-5">
      <div>
        <div className="overline">{t("dashboard.workflow_overline")}</div>
        <h2 className="font-display text-xl leading-tight mt-1">
          {t("dashboard.workflow_headline")}
        </h2>
        <p className="text-xs text-[var(--jc-text-soft)] mt-1">
          {t("dashboard.workflow_body")}
        </p>
      </div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
      {DASHBOARD_STEPS.map((step) => (
        <StepNode key={step.key} step={step} testidPrefix="workflow-strip" />
      ))}
    </div>
  </section>
  );
};

// ---------------------------------------------------------------------------
// Project lifecycle timeline
// ---------------------------------------------------------------------------
// Uses ONLY data already loaded by ProjectDetail — no new API calls.
// A stage is "reached" when the corresponding array has at least one item
// for this project. Unbuilt modules always show "coming-next".
//
// Post-QA UX refinement · Project Lifecycle Continuity
// -----------------------------------------------------
// Each stage that is BUILT is now a clickable node that keeps the
// contractor inside the current project:
//   * 0 items — Estimate stage jumps into the create-estimate flow
//               pre-filled with ?project=pid. Other stages with no
//               matching create-in-project entry point (proposal,
//               contract, work-order, invoice, closeout) stay non-
//               clickable rather than dumping the contractor into a
//               global list.
//   * 1 item  — jumps directly to that specific item's detail page.
//   * N items — expands a compact inline chooser scoped to THIS
//               project. The chooser is contractor-triggered and
//               closes when the contractor picks or cancels.
// The global lists remain the correct destination when the contractor
// intentionally uses the left-hand navigation, which is untouched
// here.
// ---------------------------------------------------------------------------
export const ProjectLifecycleTimeline = ({
  projectId,
  estimates = [],
  proposals = [],
  contracts = [],
  invoices = [],
  updates = [],
  workOrders = [],
  closeoutPackets = [],
}) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState(null); // which stage's inline chooser is open

  // Build stage descriptors. ``lineTo(item)`` returns the direct URL
  // for a single item. ``renderLine(item)`` produces the short label
  // shown in the multi-item chooser. ``createTo`` (optional) is used
  // when 0 items exist and a project-scoped create flow exists.
  const stages = [
    {
      key: "estimate", labelKey: "dashboard.step_estimate", built: true,
      items: estimates,
      lineTo: (i) => `/app/estimates/${i.id}`,
      renderLine: (i) => `${i.line_items?.length || 0} \u00b7 ${localizeStageStatus(i.status, t)}`,
      createTo: projectId ? `/app/estimates/new?project=${projectId}` : null,
    },
    {
      key: "proposal", labelKey: "dashboard.step_proposal", built: true,
      items: proposals,
      lineTo: (i) => `/app/proposals/${i.id}`,
      renderLine: (i) => localizeStageStatus(i.status, t),
    },
    {
      key: "contract", labelKey: "dashboard.step_contract", built: true,
      items: contracts,
      lineTo: (i) => `/app/contracts/${i.id}`,
      renderLine: (i) => localizeStageStatus(i.status, t),
    },
    {
      key: "customer-updates", labelKey: "dashboard.step_customer_updates", built: true,
      items: updates,
      lineTo: (i) => `/app/customer-updates/${i.id}`,
      renderLine: (i) => `${i.update_type || "update"} (${(i.language || "en").toUpperCase()})`,
    },
    { key: "change-orders", labelKey: "dashboard.step_change_orders", built: false, items: [] },
    {
      key: "work-orders", labelKey: "dashboard.step_work_orders", built: true,
      items: workOrders,
      lineTo: (i) => `/app/work-orders/${i.id}`,
      renderLine: (i) => `${i.status || "draft"}`,
    },
    {
      key: "invoice", labelKey: "dashboard.step_invoice", built: true,
      items: invoices,
      lineTo: (i) => `/app/invoices/${i.id}`,
      renderLine: (i) => `${i.invoice_type || "invoice"} \u00b7 ${i.payment_status || "unpaid"}`,
    },
    {
      key: "closeout", labelKey: "dashboard.step_closeout", built: true,
      items: closeoutPackets,
      lineTo: (i) => `/app/closeout/${i.id}`,
      renderLine: (i) => `${i.status || "draft"}`,
    },
  ];

  const goStage = (stage) => {
    const items = stage.items || [];
    if (items.length === 1) {
      navigate(stage.lineTo(items[0]));
      return;
    }
    if (items.length === 0 && stage.createTo) {
      navigate(stage.createTo);
      return;
    }
    if (items.length > 1) {
      setOpenKey((k) => (k === stage.key ? null : stage.key));
    }
    // 0 items + no createTo → button is disabled; goStage won't fire.
  };

  return (
    <section
      data-testid="project-lifecycle-timeline"
      className="jc-card p-5 mb-6"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{t("dashboard.project_lifecycle_overline")}</div>
      </div>
      <div
        className="grid grid-cols-2 items-stretch gap-2 overflow-visible md:flex md:gap-1 md:overflow-x-auto"
        data-testid="lifecycle-rail-scroll"
      >
        {stages.map((stage, idx) => {
          const items = stage.items || [];
          const reached = stage.built && items.length > 0;
          const isUnbuilt = !stage.built;
          const isFuture = stage.built && !reached;
          // Only clickable when the stage is BUILT AND has either an
          // item to open or a project-scoped create entry.
          const clickable = stage.built && (items.length >= 1 || !!stage.createTo);
          const label = (
            <div
              className={`flex flex-col items-start gap-1 py-2 px-2.5 rounded-md w-full transition ${
                reached
                  ? "bg-emerald-50 border border-emerald-200"
                  : isUnbuilt
                  ? "bg-[var(--jc-bg)] border border-dashed border-[var(--jc-border)]"
                  : "bg-white border border-[var(--jc-border)]"
              } ${clickable ? "hover:border-[var(--jc-blue)]/60 hover:shadow-sm cursor-pointer" : ""}`}
            >
              <div className="flex items-center gap-1.5">
                {reached ? (
                  <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                ) : (
                  <Circle size={13} className="text-[var(--jc-text-soft)] shrink-0" />
                )}
                <span
                  className={`text-[11px] font-semibold leading-tight ${
                    reached ? "text-emerald-900" : isFuture ? "text-[var(--jc-text)]" : "text-[var(--jc-text-soft)]"
                  }`}
                >
                  {t(stage.labelKey)}
                </span>
              </div>
              {isUnbuilt && (
                <StatusPill status="coming-next" testid={`project-lifecycle-status-${stage.key}`} />
              )}
              {clickable && items.length > 1 && (
                <span
                  className="text-[10px] text-[var(--jc-text-soft)]"
                  data-testid={`project-lifecycle-count-${stage.key}`}
                >
                  {t("dashboard.lifecycle_count_items", items.length)}
                </span>
              )}
            </div>
          );
          return (
            <div
              key={stage.key}
              data-testid={`project-lifecycle-${stage.key}`}
              className="relative flex min-w-0 w-full items-center md:min-w-[130px] md:w-auto md:shrink-0"
            >
              {clickable ? (
                <button
                  type="button"
                  onClick={() => goStage(stage)}
                  data-testid={`project-lifecycle-button-${stage.key}`}
                  aria-haspopup={items.length > 1 ? "menu" : undefined}
                  aria-expanded={items.length > 1 ? (openKey === stage.key) : undefined}
                  className="w-full text-left"
                >
                  {label}
                </button>
              ) : (
                label
              )}
              {clickable && items.length > 1 && openKey === stage.key && (
                <div
                  role="menu"
                  data-testid={`project-lifecycle-chooser-${stage.key}`}
                  className="absolute z-10 top-full mt-1 left-0 w-[240px] jc-card p-2 shadow-lg bg-white border border-[var(--jc-border)]"
                >
                  <div className="overline mb-1 px-1">{t("dashboard.lifecycle_choose_one")}</div>
                  <ul className="max-h-[220px] overflow-y-auto text-sm">
                    {items.map((it) => (
                      <li key={it.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenKey(null);
                            navigate(stage.lineTo(it));
                          }}
                          data-testid={`project-lifecycle-chooser-item-${stage.key}-${it.id}`}
                          className="w-full text-left px-2 py-1.5 rounded hover:bg-[var(--jc-bg)]"
                        >
                          <div className="text-[var(--jc-text)] font-semibold text-xs truncate">
                            {stage.renderLine(it)}
                          </div>
                          <div className="text-[10px] text-[var(--jc-text-soft)]">
                            {formatLifecycleDate(it.created_at)}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setOpenKey(null)}
                    data-testid={`project-lifecycle-chooser-close-${stage.key}`}
                    className="mt-1 w-full text-[10px] text-[var(--jc-text-soft)] hover:text-[var(--jc-text)] py-1"
                  >
                    {t("dashboard.lifecycle_cancel")}
                  </button>
                </div>
              )}
              {idx < stages.length - 1 && (
                <span
                  aria-hidden="true"
                  className="mx-1 hidden text-[var(--jc-text-soft)] text-xs shrink-0 md:block"
                >
                  →
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

// Compact status localizer used by the chooser lines. Falls back to
// the raw status string so a new backend status never renders blank.
const localizeStageStatus = (status, t) => {
  if (!status) return "";
  const key = `status.${status}`;
  const v = t(key);
  return v && !v.startsWith("status.") ? v : status;
};

const formatLifecycleDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
};
